/**
 * AffiliateRefTracker — global, mount-once component that watches the URL
 * for ``?ref=CODE`` and:
 *   1. Validates the code with the backend (sets a 30-day HTTP cookie).
 *   2. Caches it in localStorage so Register.jsx can pass it through
 *      even if the user signs up days later from a different tab.
 *   3. Strips ``?ref`` from the URL so it doesn't get bookmarked.
 *
 * Renders nothing. Place once in App.js inside the router.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

const STORAGE_KEY = 'lq_aff_ref';
const STORAGE_TS_KEY = 'lq_aff_ref_ts';
const TTL_DAYS = 30;

export function getStashedAffiliateCode() {
    try {
        const code = localStorage.getItem(STORAGE_KEY);
        const ts = parseInt(localStorage.getItem(STORAGE_TS_KEY) || '0', 10);
        if (!code) return null;
        const ageDays = (Date.now() - ts) / 86400000;
        if (ageDays > TTL_DAYS) {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(STORAGE_TS_KEY);
            return null;
        }
        return code;
    } catch { return null; }
}

export default function AffiliateRefTracker() {
    const { search, pathname } = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        const params = new URLSearchParams(search);
        const ref = (params.get('ref') || '').trim().toUpperCase();
        if (!ref) return;
        // Don't track on protected admin / teacher routes.
        if (pathname.startsWith('/admin') || pathname.startsWith('/teacher')) return;
        api.get('/affiliates/track', { params: { code: ref } })
            .then(({ data }) => {
                if (data?.ok) {
                    try {
                        localStorage.setItem(STORAGE_KEY, data.code);
                        localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
                    } catch { /* private mode */ }
                }
            })
            .catch(() => { /* silently swallow */ })
            .finally(() => {
                // Strip ?ref from the URL so subsequent navs don't re-fire.
                params.delete('ref');
                const next = params.toString();
                navigate(`${pathname}${next ? `?${next}` : ''}`, { replace: true });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);
    return null;
}
